import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DevicesIcon from '@mui/icons-material/Devices';
import { Box, CircularProgress } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { OTPTypes } from '../../../../../constants/otpTypes';
import { InvestorService } from '../../../investorService';
import { SystemSettingsService } from '../../../../../services/systemSettingsService';
import { ConfirmPasswordDialog } from '../../../../../shared/components/confirmPasswordDialog';
import { OTPAuthenticationDialog } from '../../../../../shared/components/otpAuthenticationDialog';
import { SectionWithHeading } from '../../../../../shared/components/common/sectionWithHeading';
import { ButtonWrapper } from '../../../../../shared/components/formUtilities/useButton';

export const TrustedDevices = () => {
	const investorService = new InvestorService();
	const systemSettingsService = new SystemSettingsService();
	const { t } = useTranslation();

	const [devices, setDevices] = useState([]);
	const [loading, setLoading] = useState(true);
	const [selectedDeviceId, setSelectedDeviceId] = useState(null);
	const [showOTPAuth, setShowOTPAuth] = useState(false);
	const [showPasswordDialog, setShowPasswordDialog] = useState(false);

	const settings = () => systemSettingsService.systemSettings();

	useEffect(() => {
		fetchDevices();
	}, []);

	const fetchDevices = () => {
		setLoading(true);
		investorService
			.getTrustedDevices()
			.then((resp) => {
				setDevices(resp.data || []);
				setLoading(false);
			})
			.catch(() => {
				setLoading(false);
			});
	};

	const onForgetClick = (deviceId) => {
		setSelectedDeviceId(deviceId);
		if (settings()?.enabledOtp) {
			setShowOTPAuth(true);
		} else {
			setShowPasswordDialog(true);
		}
	};

	const onCloseOTPAuth = (confirmed) => {
		setShowOTPAuth(false);
		if (confirmed) {
			doRemoveDevice(selectedDeviceId);
		}
		setSelectedDeviceId(null);
	};

	const onPasswordDialogClose = (confirmed) => {
		setShowPasswordDialog(false);
		if (confirmed) {
			doRemoveDevice(selectedDeviceId);
		}
		setSelectedDeviceId(null);
	};

	const doRemoveDevice = (id) => {
		investorService
			.removeTrustedDevice(id)
			.then(() => {
				setDevices((prev) => prev.filter((d) => d.id !== id));
			})
			.catch(() => {});
	};

	const formatDate = (dateString) => {
		if (!dateString) return '';
		try {
			return new Date(dateString).toLocaleDateString(undefined, {
				year: 'numeric',
				month: 'short',
				day: 'numeric',
			});
		} catch {
			return dateString;
		}
	};

	return (
		<>
			<SectionWithHeading
				heading={
					<Box className="flex items-center">
						<span className="mx-2">{t('trustedDevices')}</span>
					</Box>
				}
				children={
					<Box className="flex flex-col space-y-4">
						<p className="!text-[14px]">{t('trustedDevicesDescription')}</p>
						{loading ? (
							<Box className="flex justify-center py-4">
								<CircularProgress size={28} />
							</Box>
						) : devices.length === 0 ? (
							<Box className="flex flex-col items-center justify-center py-6 space-y-2">
								<DevicesIcon className="!text-gray-400 !text-[48px]" />
								<p className="!text-[14px] text-gray-500">{t('noTrustedDevices')}</p>
							</Box>
						) : (
							<Box className="flex flex-col space-y-2">
								{devices.map((device) => (
									<Box
										key={device.id}
										className="flex justify-between items-center p-3 rounded border border-gray-200">
										<Box className="flex items-center space-x-3">
											<DevicesIcon className="!text-primary" />
											<Box className="flex flex-col mx-2">
												<span className="font-[500] text-sm">{device.deviceName}</span>
												<span className="text-xs text-gray-500">
													{t('lastUsed')}: {formatDate(device.lastUsedAt)}
												</span>
											</Box>
										</Box>
										<ButtonWrapper
											classList="!danger-bg"
											icon={<DeleteOutlineIcon />}
											label={t('forget')}
											onClick={() => onForgetClick(device.id)}
										/>
									</Box>
								))}
							</Box>
						)}
					</Box>
				}
			/>
			{showOTPAuth ? (
				<OTPAuthenticationDialog
					type={OTPTypes.DeviceForget}
					state={showOTPAuth}
					bypassDeviceTrust
					onClose={(confirmed) => {
						onCloseOTPAuth(confirmed);
					}}
				/>
			) : null}
			{showPasswordDialog ? (
				<ConfirmPasswordDialog
					state={showPasswordDialog}
					onClose={(confirmed) => {
						onPasswordDialogClose(confirmed);
					}}
				/>
			) : null}
		</>
	);
};
